/**
 * QuestPanel — 每日/每週任務面板
 */

import { useEffect, useState, useCallback } from 'react';
import { useQuestStore } from '../../store/questStore';
import { useUIStore } from '../../store/uiStore';
import type { PlayerQuestView } from '../../services/QuestAPI';

// Condition display map
const CONDITION_ICONS: Record<string, string> = {
  wins: '🏆',
  games_played: '🎮',
  games_with_faction: '⚔️',
  units_summoned: '📜',
  combat_damage: '💥',
};

export function QuestPanel() {
  const { goBack } = useUIStore();
  const {
    quests,
    isLoading,
    isClaiming,
    error,
    lastClaim,
    fetchQuests,
    claim,
    clearLastClaim,
    clearError,
  } = useQuestStore();

  const [claimMessage, setClaimMessage] = useState<string | null>(null);

  useEffect(() => {
    void fetchQuests();
  }, [fetchQuests]);

  useEffect(() => {
    if (lastClaim) {
      setClaimMessage(`獲得 ${String(lastClaim.coins_earned)} 金幣！`);
      const timer = setTimeout(() => {
        setClaimMessage(null);
        clearLastClaim();
      }, 3000);
      return () => { clearTimeout(timer); };
    }
  }, [lastClaim, clearLastClaim]);

  const handleClaim = useCallback((questId: string) => {
    void claim(questId);
    // Refresh wallet in shop store if available
  }, [claim]);

  const dailyQuests = quests.filter(q => q.type === 'daily');
  const weeklyQuests = quests.filter(q => q.type === 'weekly');

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={goBack}>
          ← 返回
        </button>
        <h1 style={styles.title}>📋 每日任務</h1>
        {claimMessage && (
          <span style={styles.claimMessage}>{claimMessage}</span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={styles.errorBanner}>
          <span>{error}</span>
          <button style={styles.dismissButton} onClick={clearError}>✕</button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div style={styles.loading}>載入中...</div>
      )}

      {/* Content */}
      {!isLoading && (
        <div style={styles.content}>
          {/* Daily Quests */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              🌅 每日任務
              <span style={styles.sectionSubtitle}>（每日重置）</span>
            </h2>
            <div style={styles.questList}>
              {dailyQuests.length === 0 && (
                <div style={styles.empty}>暫無每日任務</div>
              )}
              {dailyQuests.map(q => (
                <QuestCard
                  key={q.id}
                  quest={q}
                  onClaim={(id) => { handleClaim(id); }}
                  isClaiming={isClaiming}
                />
              ))}
            </div>
          </div>

          {/* Weekly Quests */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              📅 每週任務
              <span style={styles.sectionSubtitle}>（每週一重置）</span>
            </h2>
            <div style={styles.questList}>
              {weeklyQuests.length === 0 && (
                <div style={styles.empty}>暫無每週任務</div>
              )}
              {weeklyQuests.map(q => (
                <QuestCard
                  key={q.id}
                  quest={q}
                  onClaim={(id) => { handleClaim(id); }}
                  isClaiming={isClaiming}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestCard({
  quest,
  onClaim,
  isClaiming,
}: {
  quest: PlayerQuestView;
  onClaim: (id: string) => void;
  isClaiming: boolean;
}) {
  const icon = CONDITION_ICONS[quest.condition] ?? '❓';
  const progressPct = Math.min((quest.progress / quest.target) * 100, 100);
  const canClaim = quest.completed && !quest.claimed;

  return (
    <div style={{
      ...styles.questCard,
      borderLeftColor: quest.claimed ? '#555' : quest.completed ? '#44ff44' : '#4a6a8a',
      opacity: quest.claimed ? 0.6 : 1,
    }}>
      <div style={styles.questMain}>
        <div style={styles.questHeader}>
          <span style={styles.questIcon}>{icon}</span>
          <div style={styles.questInfo}>
            <span style={styles.questTitle}>{quest.title}</span>
            <span style={styles.questDesc}>{quest.description}</span>
          </div>
        </div>

        {/* Progress bar */}
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div style={{
              ...styles.progressFill,
              width: `${String(progressPct)}%`,
              backgroundColor: quest.completed ? '#44ff44' : '#4a8adf',
            }} />
          </div>
          <span style={styles.progressText}>
            {quest.progress}/{quest.target}
          </span>
        </div>
      </div>

      {/* Rewards + Claim */}
      <div style={styles.questRight}>
        <div style={styles.rewards}>
          {quest.reward_coins > 0 && (
            <span style={styles.rewardItem}>🪙 {quest.reward_coins}</span>
          )}
          {quest.reward_dust > 0 && (
            <span style={styles.rewardItem}>✨ {quest.reward_dust}</span>
          )}
          {quest.reward_packs > 0 && (
            <span style={styles.rewardItem}>🎴 {quest.reward_packs}</span>
          )}
        </div>

        {canClaim && (
          <button
            style={styles.claimButton}
            onClick={() => { onClaim(quest.id); }}
            disabled={isClaiming}
          >
            {isClaiming ? '...' : '領取'}
          </button>
        )}
        {quest.claimed && (
          <span style={styles.claimedBadge}>✓ 已領取</span>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Arial, sans-serif',
    color: '#ffffff',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#252545',
    borderBottom: '1px solid #3a3a5a',
    gap: '16px',
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  claimMessage: {
    marginLeft: 'auto',
    color: '#44ff44',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 24px',
    backgroundColor: '#4a2a2a',
    color: '#ff8888',
  },
  dismissButton: {
    background: 'none',
    border: 'none',
    color: '#ff8888',
    cursor: 'pointer',
    fontSize: '16px',
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#888888',
    fontSize: '18px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%',
  },
  section: {},
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '16px',
    color: '#ffffff',
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#888888',
    fontWeight: 'normal',
    marginLeft: '8px',
  },
  questList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  empty: {
    color: '#666666',
    fontSize: '14px',
    textAlign: 'center',
    padding: '24px',
  },
  questCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: '#252545',
    borderLeft: '4px solid',
    borderRadius: '8px',
    gap: '16px',
  },
  questMain: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  questHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  questIcon: {
    fontSize: '24px',
  },
  questInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  questTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  questDesc: {
    fontSize: '13px',
    color: '#aaaaaa',
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  progressBar: {
    flex: 1,
    height: '8px',
    backgroundColor: '#3a3a5a',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '13px',
    color: '#cccccc',
    minWidth: '50px',
    textAlign: 'right',
  },
  questRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
    minWidth: '120px',
  },
  rewards: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  rewardItem: {
    fontSize: '14px',
    color: '#ffdd88',
    whiteSpace: 'nowrap',
  },
  claimButton: {
    padding: '8px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    backgroundColor: '#3a7a3a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  claimedBadge: {
    fontSize: '13px',
    color: '#888888',
  },
};
